import React, { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";

const YogaNidraOverview = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const [selectedCard, setSelectedCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const cards = [
    {
      title: "Ancient Practice",
      description: (
        <>
          Yoga Nidra, known as psychic sleep, is derived from the ancient, traditional tantric practice of Nyasa, where specific mantras were placed on different body parts to awaken subtle energies.
        </>
      ),
      image: "/image-1.png",
      bgColor: "bg-[#43A047]/5",
      borderColor: "border-[#43A047]/30",
      hoverColor: "hover:bg-[#43A047]/30",
      gradient: "from-green-50 to-white",
      number: "01",
      modalContent: (
        <>
          Yoga Nidra, known as psychic sleep, is derived from the ancient, traditional tantric practice of Nyasa, where specific mantras were placed on different body parts to awaken subtle energies. This evolved into Yoga Nidra, which uses awareness and intention to move through the body to access deeper states of relaxation and inner connection. Modern science confirms its effectiveness in stress management and enhanced well-being and creativity.
        </>
      )
    },
    {
      title: "Conscious Relaxation",
      description: (
        <>
          Yoga Nidra practice systematically guides the brain into a hypnagogic state between wakefulness and sleep, activating a shift to the parasympathetic nervous system - the body's natural rest-and-digest mode.
        </>
      ),
      image: "/image-2.png",
      bgColor: "bg-[#039EFF]/5",
      borderColor: "border-[#039EFF]/30",
      hoverColor: "hover:bg-[#039EFF]/30",
      gradient: "from-blue-50 to-white",
      number: "02",
      modalContent: (
        <>
          Yoga Nidra practice systematically guides the brain into a hypnagogic state between wakefulness and sleep, activating a shift to the parasympathetic nervous system - the body's natural rest-and-digest mode. As the nervous system resets, it reduces cortisol levels, removes fatigue and supports emotional resilience. Regular practice supports long-term regulation of the stress response, helping reduce anxiety and fear.
        </>
      )
    },
    {
      title: "Subconscious Clearing",
      description: (
        <>
          Yoga Nidra is a powerful practice for accessing and gently reshaping the subconscious mind. In this threshold state, samskaras - limiting beliefs, emotional patterns and mental imprints can surface and release.
        </>
      ),
      image: "/image-3.png",
      bgColor: "bg-[#896218]/5",
      borderColor: "border-[#896218]/30",
      hoverColor: "hover:bg-[#896218]/30",
      gradient: "from-orange-50 to-white",
      number: "03",
      modalContent: (
        <>
          Yoga Nidra is a powerful practice for accessing and gently reshaping the subconscious mind. In this threshold state, samskaras - limiting beliefs, emotional patterns and mental imprints can surface and release. Repeating a sankalpa in this receptive state helps plant new, empowering beliefs into the subconscious. Regular practice supports neuroplasticity by reinforcing calm and focus, while reducing reactive patterns and negative thought loops.
        </>
      )
    },
    {
      title: "Healing Power",
      description: (
        <>
          Relaxation with self-awareness is fundamental to healing. Yoga Nidra activates the body's natural healing mechanisms by guiding into a state of stillness from doing to being.
        </>
      ),
      image: "/image-4.png",
      bgColor: "bg-[#43A047]/5",
      borderColor: "border-[#43A047]/25",
      hoverColor: "hover:bg-[#43A047]/25",
      gradient: "from-green-50 to-blue-50",
      number: "04",
      modalContent: (
        <>
          Relaxation with self-awareness is fundamental to healing. Yoga Nidra activates the body's natural healing mechanisms by guiding into a state of stillness from doing to being. Stress lessens, sleep improves, and a sense of peace deepens. With consistent practice over time, stillness evolves from a fleeting moment, into a steady healing presence within, lighting the path towards spiritual progress.
        </>
      )
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
      },
    },
  };

  const headerVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
      },
    },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <section
      ref={ref}
      id="yoga-nidra-overview"
      className="relative py-20  bg-gradient-to-b from-white to-slate-50/50 overflow-hidden"
    >
      {/* Simple, Professional Background Pattern */}
      <div className="absolute inset-0 z-0">
        {/* Subtle grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.08]"
          style={{
            backgroundImage: `linear-gradient(#43A047 1px, transparent 1px),
                             linear-gradient(to right, #43A047 1px, transparent 1px)`,
            backgroundSize: '64px 64px'
          }}
        ></div>

        {/* Subtle diagonal lines */}
        <div 
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage: `repeating-linear-gradient(
              45deg,
              #039EFF,
              #039EFF 1px,
              transparent 1px,
              transparent 40px
            )`
          }}
        ></div>

        {/* Clean gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/80 via-white/40 to-transparent"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          {/* Section Header */}
          <motion.div
            variants={headerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mb-20"
          >
            {/* Category badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-200/80 border border-slate-300 mb-6 backdrop-blur-sm">
              <div className="w-2 h-2 rounded-full bg-slate-600 animate-pulse"></div>
              <span className="text-sm font-medium text-slate-700 uppercase tracking-wider">
                ANCIENT PRACTICE
              </span>
            </div>

            <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-800 mb-8">
         What is Yoga Nidra?
            </h2>

            <p className="text-xl md:text-2xl text-slate-600 max-w-6xl mx-auto leading-relaxed font-light">
            Yoga Nidra is a practice of pratyahara, the fifth limb of Ashtanga Yoga, where senses are gently withdrawn and awareness turns inward. Unlike physically demanding practices, Yoga Nidra is done lying in savasana(corpse pose), and benefits are subtle yet profound which become more noticeable with regular practice.  
            </p>

            {/* Decorative divider */}
            <div className="flex items-center justify-center mt-8 gap-4">
              <div className="w-16 h-[1px] bg-slate-300"></div>
              <div className="w-2 h-2 rounded-full bg-slate-400"></div>
              <div className="w-16 h-[1px] bg-slate-300"></div>
            </div>
          </motion.div>

          {/* Cards Grid - Single Row */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8"
          >
            {cards.map((card, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Card 
                  className={`group relative bg-gradient-to-br ${card.gradient} ${card.borderColor} hover:shadow-xl transition-all duration-500 overflow-hidden h-full cursor-pointer border-2`}
                  onClick={() => setSelectedCard(index)}
                >
                  <CardContent className="p-0 relative z-10 h-full flex flex-col">
                    {/* Image section */}
                    <div className="relative h-40 lg:h-48 overflow-hidden">
                      <img
                        src={card.image}
                        alt={card.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      {/* Gradient overlay on image */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                    </div>

                    {/* Content section */}
                    <div className="p-6 lg:p-8 flex-1 flex flex-col">
                      {/* Title with improved typography */}
                      <h3 className="font-heading text-xl lg:text-2xl font-bold text-slate-800 mb-3 group-hover:text-slate-900 transition-colors duration-300">
                        {card.title}
                      </h3>

                      {/* Description with better readability */}
                      <p className="text-slate-600 leading-relaxed text-sm flex-grow mb-4 font-light">
                        {card.description}
                      </p>

                      {/* Modern progress indicator */}
                      <div className="flex items-center gap-3 pt-4 border-t border-slate-300/50">
                        <div className="flex-1 h-1 bg-slate-300/50 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${card.bgColor.replace('/20', '').replace('/15', '')} rounded-full transition-all duration-700 group-hover:w-full w-1/3`}
                          ></div>
                        </div>
                        <div className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                          Learn More
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Bottom CTA section */}
          <motion.div
            variants={headerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="text-center mt-20"
          >
            <div className="max-w-3xl mx-auto">
              {/* <p className="text-slate-600 text-lg leading-relaxed mb-8">
                Ready to experience the profound benefits of Yoga Nidra? Our
                guided sessions are designed to help you unlock deep rest and
                inner transformation.
              </p> */}

              {/* Call to action with solid color styling */}
              <div 
                className="inline-flex items-center gap-3 px-8 py-4 bg-[#43A047] hover:bg-[#039EFF] rounded-full text-white font-medium hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer"
                onClick={() => navigate('/contact')}
              >
                <span>Begin Your Journey</span>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Modal */}
      {selectedCard !== null && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setSelectedCard(null)}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto overflow-x-visible shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {/* Close button */}
              <button
                onClick={() => setSelectedCard(null)}
                className="absolute top-4 right-4 z-10 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors duration-200 shadow-lg"
              >
                <svg className="w-5 h-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Modal content */}
              <div className="flex flex-col">
                {/* Image section */}
                <div className="relative w-full aspect-[4/3] h-56 md:h-80 lg:h-96">
                  <img
                    src={cards[selectedCard].image}
                    alt={cards[selectedCard].title}
                    className="w-full h-full object-contain rounded-t-2xl"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent pointer-events-none rounded-t-2xl"></div>
                </div>

                {/* Content section */}
                <div className="w-full p-8 md:p-12 flex flex-col justify-center">
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-slate-100 text-slate-600 text-sm font-medium rounded-full">
                      {cards[selectedCard].number}
                    </span>
                  </div>
                  
                  <h3 className="font-heading text-3xl md:text-4xl font-bold text-slate-800 mb-6">
                    {cards[selectedCard].title}
                  </h3>
                  
                  <p className="text-slate-600 leading-relaxed text-lg font-light">
                    {cards[selectedCard].modalContent}
                  </p>

                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <button 
                      className="inline-flex items-center gap-2 px-6 py-3 bg-[#43A047] hover:bg-[#039EFF] text-white font-medium rounded-full transition-all duration-300"
                      onClick={() => navigate('/contact')}
                    >
                      <span>Start Practice</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
};

export default YogaNidraOverview;
